import React, { useEffect, useMemo, useState } from "react";
import { ListPageLayout } from "@/components/cadastro/ListPageLayout";
import { DataTable, ColumnDef } from "@/components/cadastro/DataTable";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Trash, Upload, Download, Plus, Info } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { MaterialCache, type Material as CacheMaterial } from "@/services/materialCache";

// Shape da tela: o mesmo do MaterialCache (tabela `materiais` no Supabase),
// com o alias atualizadoEm -> updatedAt mantido no render.
type Status = "Ativo" | "Inativo";
type Material = CacheMaterial;

const CATEGORIAS_BASE = ["Elétrica", "Hidráulica", "Ar condicionado"];
const UNIDADES_BASE = ["un", "m", "m²", "m³", "kg", "L"];

const Materiais = () => {
  const [search, setSearch] = useState("");
  // Fonte única: materiais reais do usuário (Supabase via MaterialCache)
  const [rows, setRows] = useState<Material[]>(() => MaterialCache.getSessionMaterials());
  const [loading, setLoading] = useState(false);

  // Filters
  const [fCategorias, setFCategorias] = useState<string[]>([]);

  // Modals
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Material | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Material | null>(null);
  const [openCatalog, setOpenCatalog] = useState(false);

  useEffect(() => {
    document.title = "Cadastro - Materiais";
    setRows(MaterialCache.getSessionMaterials());
  }, []);

  // Derived
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(r => {
      const hit = !term || `${r.nome} ${r.codigo ?? ""} ${r.categoria}`.toLowerCase().includes(term);
      const cat = !fCategorias.length || fCategorias.includes(r.categoria);
      return hit && cat;
    });
  }, [rows, search, fCategorias]);

  const categorias = useMemo(() => Array.from(new Set([...CATEGORIAS_BASE, ...rows.map(r => r.categoria)])).sort(), [rows]);
  const unidades = useMemo(() => Array.from(new Set([...UNIDADES_BASE, ...rows.map(r => r.unidade)])).sort(), [rows]);

  // Helpers
  const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
  const exportCSV = () => {
    const headers = ["codigo","nome","categoria","unidade","status","sinonimos","atualizadoEm"];
    const lines = [headers.join(","), ...filtered.map(r => [r.codigo ?? "", r.nome, r.categoria, r.unidade, r.status, r.sinonimos.join(";").replace(/,/g, ";"), r.updatedAt].map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))];
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "materiais.csv"; a.click(); URL.revokeObjectURL(url);
    toast({ title: "Exportado", description: "CSV gerado conforme filtros atuais." });
  };

  const openNew = (prefill?: Partial<Material>) => {
    const now = new Date().toISOString();
    setEditing({ id: "", nome: "", categoria: "", unidade: "", status: "Ativo", sinonimos: [], createdAt: now, updatedAt: now, ...prefill });
    setOpenForm(true);
  };

  const saveEditing = () => {
    if (!editing) return;
    // validations
    if (!editing.nome || !editing.categoria || !editing.unidade) {
      toast({ title: "Preencha os campos obrigatórios", description: "Nome, Categoria e Unidade são obrigatórios.", variant: "destructive" as any });
      return;
    }
    const dup = rows.some(r => r.id !== editing.id && ((r.nome.trim().toLowerCase() === editing.nome.trim().toLowerCase() && r.unidade === editing.unidade) || (!!editing.codigo && r.codigo && r.codigo === editing.codigo)));
    if (dup) {
      toast({ title: "Duplicidade detectada", description: "Já existe material com mesmo Nome+Unidade ou Código.", variant: "destructive" as any });
      return;
    }
    // Persistência real (Supabase via MaterialCache, write-through)
    const exists = editing.id && rows.some(r => r.id === editing.id);
    if (exists) {
      MaterialCache.updateSessionMaterial(editing);
      toast({ title: "Material atualizado" });
    } else {
      const { id, createdAt, updatedAt, ...rest } = editing;
      MaterialCache.addSessionMaterial(rest);
      toast({ title: "Material adicionado" });
    }
    setRows(MaterialCache.getSessionMaterials());
    setOpenForm(false); setEditing(null);
  };

  const columns: ColumnDef<Material>[] = [
    { key: "codigo", header: "Código", sortable: true },
    { key: "nome", header: "Nome do material", sortable: true },
    { key: "categoria", header: "Categoria", sortable: true },
    { key: "unidade", header: "Unidade", sortable: true },
    { key: "status", header: "Status", sortable: true },
    { key: "sinonimos", header: "Sinônimos", render: (_value, row) => (
      row.sinonimos?.length ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary">{row.sinonimos.length} termos</Badge>
            </TooltipTrigger>
            <TooltipContent>
              <ul className="text-sm max-w-xs list-disc pl-4">
                {row.sinonimos.map((s, i)=> <li key={i}>{s}</li>)}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : <span className="text-muted-foreground">-</span>
    ) },
    { key: "updatedAt", header: "Atualizado em", sortable: true, render: (v) => <span>{formatDate(String(v))}</span> },
    { key: "id", header: "Ações", className: "w-[120px] text-right", render: (_v, row) => (
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="icon" onClick={(e)=> { e.stopPropagation(); setEditing(row); setOpenForm(true); }} aria-label="Editar" title="Editar">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={(e)=> { e.stopPropagation(); setConfirmDelete(row); }} aria-label="Excluir" title="Excluir">
          <Trash className="h-4 w-4" />
        </Button>
      </div>
    ) },
  ];

  // Filters UI
  const FiltersInline = (
    <div className="flex items-center gap-2">
      {/* Categoria multiseleção */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-11 sm:h-9">Categoria {fCategorias.length ? `(${fCategorias.length})` : ""}</Button>
        </PopoverTrigger>
        <PopoverContent className="w-56" align="start">
          <div className="space-y-2">
            {categorias.map((c)=> (
              <label key={c} className="flex items-center gap-2">
                <Checkbox checked={fCategorias.includes(c)} onCheckedChange={(v)=> setFCategorias(prev => v ? [...prev, c] : prev.filter(x=> x!==c))} />
                <span className="text-sm">{c}</span>
              </label>
            ))}
            {!!fCategorias.length && (
              <Button variant="ghost" size="sm" className="h-11 sm:h-9" onClick={()=> setFCategorias([])}>Limpar</Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

    </div>
  );

  // Catálogo de referência do sistema (o mesmo usado no RDO)
  const catalogItems: Material[] = MaterialCache.getCatalogMaterials();
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSel, setCatalogSel] = useState<Record<string, boolean>>({});
  const catalogFiltered = useMemo(()=> catalogItems.filter(c => !catalogSearch || `${c.nome} ${c.codigo} ${c.categoria}`.toLowerCase().includes(catalogSearch.toLowerCase())), [catalogItems, catalogSearch]);

  const importOneFromCatalog = (m: Material) => {
    // prefill new form
    openNew({ nome: m.nome, codigo: m.codigo, categoria: m.categoria, unidade: m.unidade, sinonimos: m.sinonimos, status: m.status });
    setOpenCatalog(false);
  };
  const importSelectedFromCatalog = () => {
    const selected = catalogFiltered.filter(i => catalogSel[i.id]);
    if (!selected.length) { toast({ title: "Selecione ao menos um item" }); return; }
    let added = 0, skipped = 0;
    selected.forEach(m => {
      const dup = rows.some(r => (r.nome.trim().toLowerCase() === m.nome.trim().toLowerCase() && r.unidade === m.unidade) || (!!m.codigo && r.codigo === m.codigo));
      if (dup) { skipped++; return; }
      // Persiste no Supabase via cache (mesma origem do RDO)
      MaterialCache.importFromCatalog(m.id);
      added++;
    });
    setRows(MaterialCache.getSessionMaterials());
    setOpenCatalog(false); setCatalogSel({});
    toast({ title: "Importação concluída", description: `${added} adicionados, ${skipped} ignorados por duplicidade.` });
  };

  return (
    <section>
      <ListPageLayout
        title="Materiais"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome, código, categoria…"
        onAdd={() => openNew()}
        inlineFilters
        filters={FiltersInline}
        actionsExtra={<Button variant="secondary" size="sm" className="h-11 sm:h-9" onClick={()=> setOpenCatalog(true)}>Importar da lista do sistema</Button>}
        onExportClick={exportCSV}
        onImportClick={()=> toast({ title: "Importação por arquivo", description: "Mapeamento de colunas em breve." })}
      >
        {loading ? (
          <div className="space-y-2">
            <div className="h-10 w-full rounded-md bg-muted animate-pulse" />
            <div className="h-10 w-full rounded-md bg-muted animate-pulse" />
            <div className="h-10 w-2/3 rounded-md bg-muted animate-pulse" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed rounded-md p-8 text-center">
            <p className="mb-4 text-muted-foreground">Nenhum material cadastrado</p>
            <div className="flex items-center justify-center gap-2">
              <Button onClick={()=> openNew()}><Plus className="h-4 w-4" /> Adicionar</Button>
              <Button variant="secondary" onClick={()=> setOpenCatalog(true)}><Upload className="h-4 w-4" /> Importar da lista do sistema</Button>
            </div>
          </div>
        ) : (
          <DataTable
            data={filtered}
            setData={setRows}
            columns={columns}
            pageSize={10}
            dense
            onRowClick={(row)=> { setEditing(row); setOpenForm(true); }}
          />
        )}
      </ListPageLayout>

      {/* Novo/Editar Material */}
      <Dialog open={openForm} onOpenChange={(o)=> { setOpenForm(o); if(!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{editing && rows.some(r => r.id === editing.id) ? "Editar Material" : "Novo Material"}</DialogTitle>
            <DialogDescription>
              Nome, categoria e unidade são obrigatórios.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-sm">Nome*</label>
                <Input value={editing.nome} onChange={(e)=> setEditing({ ...editing, nome: e.target.value })} placeholder="Nome do material" />
              </div>
              <div>
                <label className="text-sm">Código interno</label>
                <Input value={editing.codigo ?? ""} onChange={(e)=> setEditing({ ...editing, codigo: e.target.value })} placeholder="Ex.: MAT-123" />
              </div>
              <div>
                <label className="text-sm">Categoria*</label>
                <Select value={editing.categoria} onValueChange={(v)=> setEditing({ ...editing, categoria: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm">Unidade de medida*</label>
                <Select value={editing.unidade} onValueChange={(v)=> setEditing({ ...editing, unidade: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {unidades.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm">Sinônimos</label>
                <SynonymsEditor value={editing.sinonimos} onChange={(v)=> setEditing({ ...editing, sinonimos: v })} />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm">Descrição</label>
                <Textarea value={editing.descricao ?? ""} onChange={(e)=> setEditing({ ...editing, descricao: e.target.value })} placeholder="Opcional" />
              </div>
              <div>
                <label className="text-sm">Status</label>
                <Select value={editing.status} onValueChange={(v: any)=> setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ativo">Ativo</SelectItem>
                    <SelectItem value="Inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm">Anexos</label>
                <Input type="file" multiple accept=".pdf,image/*" />
              </div>
              <div className="md:col-span-2 text-xs text-muted-foreground">Atualizado em: {formatDate(editing.updatedAt)}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=> { setOpenForm(false); setEditing(null); }}>Cancelar</Button>
            <Button onClick={saveEditing}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar Exclusão */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o)=> !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir material?</AlertDialogTitle>
          </AlertDialogHeader>
          <p className="text-sm text-muted-foreground">Essa ação não pode ser desfeita.</p>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={()=> { if(confirmDelete){ MaterialCache.deleteSessionMaterial(confirmDelete.id); setRows(MaterialCache.getSessionMaterials()); toast({ title: "Material excluído" }); } setConfirmDelete(null); }}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Catálogo (Importar da lista do sistema) */}
      <Dialog open={openCatalog} onOpenChange={setOpenCatalog}>
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Importar materiais pré-cadastrados</DialogTitle>
            <DialogDescription>
              Selecione os materiais do catálogo do sistema para copiar ao seu cadastro.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input value={catalogSearch} onChange={(e)=> setCatalogSearch(e.target.value)} placeholder="Buscar por nome/código" />
            <Button variant="secondary" onClick={importSelectedFromCatalog}><Download className="h-4 w-4" /> Importar selecionados</Button>
          </div>
          <div className="w-full overflow-x-auto rounded-md border border-border mt-3">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catalogFiltered.map((m)=> (
                  <TableRow key={m.id}>
                    <TableCell><Checkbox checked={!!catalogSel[m.id]} onCheckedChange={(v)=> setCatalogSel(s => ({ ...s, [m.id]: !!v }))} /></TableCell>
                    <TableCell>{m.nome}</TableCell>
                    <TableCell>{m.categoria}</TableCell>
                    <TableCell>{m.unidade}</TableCell>
                    <TableCell>{m.codigo}</TableCell>
                    <TableCell className="text-right"><Button size="sm" className="h-11 sm:h-9" variant="ghost" onClick={()=> importOneFromCatalog(m)}>Importar este material</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

function SynonymsEditor({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim(); if (!v) return; onChange([...(value ?? []), v]); setInput("");
  };
  const remove = (i: number) => onChange(value.filter((_, idx)=> idx !== i));
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input value={input} onChange={(e)=> setInput(e.target.value)} onKeyDown={(e)=> { if(e.key === 'Enter'){ e.preventDefault(); add(); } }} placeholder="Digite um sinônimo e pressione Enter" />
        <Button variant="secondary" onClick={add}>Adicionar</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {value?.map((s, i)=> (
          <Badge key={i} variant="secondary" className="flex items-center gap-2">
            {s}
            <button className="ml-1 text-xs text-muted-foreground" onClick={()=> remove(i)} aria-label={`Remover ${s}`}>×</button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default Materiais;
